import SessionHelper from './sessionHelper';
import DeviceHelper from './deviceHelper';
import { getFdsRequestHeader } from './designSystem';

export type RequestOptions = {
  method: string;
  path: string;
  data?: Record<string, any>;
};

// Fractal Headers
const HeaderFractalClient = "Fc"; // Fractal-Client
const HeaderFractalInterfaceEngine = "Fie"; // Fractal-Interface-Engine
const HeaderFractalSessionID = "Fsi"; // Fractal-Session-ID
const HeaderFractalDeviceID = "Fdi"; // Fractal-Device-ID
const HeaderFractalDesignSystem = "Fds"; // Fractal-Design-System (FE-4577)

const BASE_API_PATH = '/api/v1';

let frameApiContext: { companyShortname?: string; projectShortname?: string } = {};

// Set by the iframe postMessage handler when running in frame mode under fractal-fe.
// In iframe mode, the renderer host needs the workspace prefix in the URL path:
//   {origin}/{company_shortname}/{project_shortname}/api/v1{path}
export function setFrameApiContext(ctx: { companyShortname?: string; projectShortname?: string }) {
  frameApiContext = { ...frameApiContext, ...ctx };
}

// True when the renderer runs inside the editor's view-mode iframe (?frame=true).
// In that mode the schema arrives by postMessage from the editor, not from the API.
export const isFrameMode = () => typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('frame');

const buildUrl = (path: string): string => {
  const { companyShortname, projectShortname } = frameApiContext;
  if (isFrameMode() && companyShortname && projectShortname) {
    return `${window.location.origin}/${companyShortname}/${projectShortname}${BASE_API_PATH}${path}`;
  }
  return `${BASE_API_PATH}${path}`;
};

export default async function request({
  method,
  path,
  data,
}: RequestOptions) {
  const url = buildUrl(path);

  const body: string | FormData = JSON.stringify(data);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=utf-8',
    [HeaderFractalClient]: 'web',
    [HeaderFractalInterfaceEngine]: 'true',
    [HeaderFractalSessionID]: SessionHelper.getOrCreateSessionId(),
    [HeaderFractalDeviceID]: DeviceHelper.getOrCreateDeviceId(),
    // FE-4577 — what this client already holds of the design system. Never
    // throws; degrades to `h=none`, which is simply a full delivery.
    // `buildUrl` makes every request from this helper same-origin by
    // construction, so there is no third party to leak the inventory hash to.
    [HeaderFractalDesignSystem]: await getFdsRequestHeader(),
  };

  const response = await fetch(url, {
    method,
    headers,
    body,
  });

  if (response.status !== 200) {
    let message = await response.text();
    if (message.startsWith('{')) {
      message = JSON.parse(message).data;
    }
    throw new Error(`${response.status} ${response.statusText}. ${message}`);
  }

  return response.json();
}
